import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Lock, Loader2 } from "lucide-react";
import Aurora from "@/components/Aurora";
import { createCheckoutSession } from "@/lib/api";
import { track } from "@/lib/analytics";

const PACKAGE_ID = "ai_infrastructure_audit_v1";

export default function AuditCheckout() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const assessmentId = location.state?.assessmentId;
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");

  async function handleCheckout() {
    setBusy(true);
    setErr("");
    track("audit_checkout_initiated");
    try {
      const { url } = await createCheckoutSession({
        package_id: PACKAGE_ID,
        assessment_id: assessmentId,
        success_url: `${window.location.origin}/audit/thank-you?session_id={CHECKOUT_SESSION_ID}`,
      });
      window.location.replace(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden grid place-items-center px-5 py-16">
      <Aurora variant="intense" />

      <div className="relative max-w-lg w-full">
        {/* Logo */}
        <div className="flex items-center gap-2 justify-center mb-10">
          <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 items-center justify-center shadow-[0_0_24px_rgba(0,229,255,0.5)]">
            <span className="text-black font-bold text-xs">FS</span>
          </span>
          <span className="font-display text-white text-lg">
            <span className="font-normal">Favor</span>{" "}
            <span className="font-bold">Studio.</span>
          </span>
        </div>

        <div
          style={{
            background: "rgba(5,10,28,0.97)",
            border: "1px solid rgba(0,229,255,0.2)",
            borderRadius: 20,
            boxShadow: "0 24px 80px rgba(0,0,0,0.8)",
            backdropFilter: "blur(28px)",
          }}
          className="p-8 sm:p-10"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6"
            style={{ border: "1px solid rgba(0,229,255,0.3)", background: "rgba(0,229,255,0.06)" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" style={{ boxShadow: "0 0 6px #00E5FF" }} />
            <span className="text-[10px] tracking-[0.25em] text-sky-300 font-semibold font-sans uppercase">Private AI Infrastructure Audit</span>
          </div>

          <h1 className="font-display text-2xl sm:text-3xl tracking-tight text-white mb-3">
            <span className="font-normal">Reserve Your </span>
            <span className="font-bold">Infrastructure Audit</span>
          </h1>
          <p className="text-zinc-400 text-sm leading-relaxed mb-8">
            Over 60–90 minutes, we'll identify your biggest business constraint and design a
            custom AI Infrastructure roadmap built specifically for your business.
          </p>

          {/* Pricing summary */}
          <div
            className="mb-8 p-5 rounded-2xl"
            style={{ background: "rgba(0,229,255,0.05)", border: "1px solid rgba(0,229,255,0.15)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-zinc-300 text-sm font-semibold">Private AI Infrastructure Audit</span>
              <span className="text-white font-bold text-lg">$397</span>
            </div>
            <div className="h-px bg-white/5 mb-3" />
            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
              <Lock size={11} />
              <span>Secure checkout &middot; One-time payment &middot; 60–90 minute session</span>
            </div>
          </div>

          {err && (
            <p className="text-rose-400 text-xs mb-4 font-sans">{err}</p>
          )}

          <button
            onClick={handleCheckout}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 py-4 px-6 rounded-full font-bold text-sm tracking-wider transition-all"
            style={{
              background: busy ? "rgba(255,255,255,0.07)" : "linear-gradient(135deg, #00E5FF 0%, #0066FF 100%)",
              border: "1px solid rgba(0,229,255,0.5)",
              color: busy ? "rgba(255,255,255,0.3)" : "#001018",
              cursor: busy ? "default" : "pointer",
            }}
          >
            {busy
              ? <><Loader2 size={15} className="animate-spin" /> REDIRECTING TO CHECKOUT…</>
              : <>PROCEED TO CHECKOUT <ArrowRight size={15} /></>}
          </button>

          <button
            onClick={() => navigate(-1)}
            className="mt-4 w-full text-center text-xs text-zinc-500 hover:text-zinc-300 transition-colors py-2"
          >
            ← Go back
          </button>
        </div>
      </div>
    </div>
  );
}
